import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import {
  fetchConfirmationLetter,
  updateConfirmationLetter,
} from "../../api/reportsApi";
import { LetterDto } from "../../types/Rows";
import { Button } from "../../components/ui/button";
import { NavigationBadge, useNavigationBadgesStore } from "../..";
import { pdf } from "@react-pdf/renderer";
import { ConfirmationletterDocGenerator, CL_SECTIONS } from "./confirmationletterDocGenerator";
import { useUserContext } from "../user/authProvider";
import { ItemPopover } from "../../components/atomic/itemPopover";
import { Modal, ModalBody, ModalHeader } from "../../components/ui/Modal";
import { field } from "../../components/ui/styles";
import { GeneratedDocumentsList, useStoreGeneratedDocument } from "../../components/generatedDocuments/GeneratedDocuments";
import { PdfDesignPicker, toVariant, usePdfDesign } from "../../components/pdfDesign/PdfDesign";
import { ConfirmationLetterDocumentV2 } from "../report/document/v2/LetterDocumentsV2";

function ConfirmationLetterContent({
  letterId,
  isModal,
}: {
  letterId: string | undefined;
  isModal?: boolean;
}) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { parsedName } = useUserContext();
  const storeCopy = useStoreGeneratedDocument(letterId);

  const [confirmationLetter, setConfirmationLetter] =
    useState<LetterDto | null>(null);
  const [footerText, setFooterText] = useState(
    "MONITORING VISIT CONFIRMATION LETTER"
  );
  const [title, setTitle] = useState(confirmationLetter?.studyName);
  const [visitType, setVisitType] = useState(confirmationLetter?.visitType);
  const [versionText, setVersionText] = useState(
    "Wersja 1.0 PL, 19 LISTOPAD 2022"
  );
  const [showExportModal, setShowExportModal] = useState(false);
  const [enabledSections, setEnabledSections] = useState<Set<string>>(
    () => new Set(CL_SECTIONS.map((s) => s.key))
  );

  const { data } = useQuery(
    ["ConfirmationLetter", letterId],
    async () => fetchConfirmationLetter(letterId),
    {
      enabled: letterId != null,
      initialData: null,
    }
  );
  const setItems = useNavigationBadgesStore((state: any) => state.setItems);

  useEffect(() => {
    if (data && !isModal) {
      setItems([
        { text: "Badania", url: "/" } as NavigationBadge,
        { text: data.studyName, url: "/study/" + data.studyId } as NavigationBadge,
        { text: data.reportName, url: "/reports/" + data.reportId } as NavigationBadge,
        { text: "Confirmation letter", url: "" } as NavigationBadge,
      ]);
    }
  }, [data, setItems, isModal]);

  const { mutate: updateLetter } = useMutation(
    async (letter: LetterDto) => updateConfirmationLetter(letterId, letter),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["ConfirmationLetter", letterId]);
      },
    }
  );

  useEffect(() => {
    if (data) {
      setConfirmationLetter(data);
      setTitle(data?.studyName);
      setVisitType(data?.reportName);
    }
  }, [data]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setConfirmationLetter((prev) => ({ ...prev, [name]: value }));
  };

  const [pdfDesign, choosePdfDesign] = usePdfDesign();
  const handleDownload = async (sections: Set<string>) => {
    const docProps = { confirmationLetter, author: parsedName, footerText, versionText, title, visitType, sections };
    const variant = toVariant(pdfDesign);
    const blob = await pdf(
      variant ? <ConfirmationLetterDocumentV2 variant={variant} {...docProps} /> : <ConfirmationletterDocGenerator {...docProps} />
    ).toBlob();
    const fileName = `Confirmation-letter-${confirmationLetter.reportName}-${confirmationLetter.resortName}-${confirmationLetter.studyName}.pdf`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowExportModal(false);
    storeCopy("CL", blob, fileName); // SR-035: kopia w historii
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (confirmationLetter) updateLetter(confirmationLetter);
  };

  const toggleSection = (key: string) => {
    setEnabledSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (!confirmationLetter) return <div>Loading...</div>;

  const inputCls = "w-full rounded-lg border border-[#EBEBEA] px-3 py-2 text-sm text-[var(--sc-text)] focus:outline-none focus:ring-1 focus:ring-[#F4A832]";
  const inputDisabledCls = "w-full rounded-lg border border-[#EBEBEA] px-3 py-2 text-sm bg-[#F7F7F5] text-[var(--sc-muted)]";
  const labelCls = "block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em] mb-1";

  const form = (
    <>
      <Modal open={showExportModal} onClose={() => setShowExportModal(false)} size="sm" offset="sm">
        <ModalHeader title="Wybierz sekcje do eksportu" onClose={() => setShowExportModal(false)} />
        <ModalBody>
          <div className="mb-2 pb-2 border-b border-[var(--sc-border)]">
            <PdfDesignPicker value={pdfDesign} onChange={choosePdfDesign} name="pdf-design-cl" />
          </div>
          <div className="flex flex-col gap-1">
            {CL_SECTIONS.map((s) => (
              <label key={s.key} className="flex items-center gap-2.5 px-2 py-1.5 rounded-[6px] hover:bg-[var(--sc-bg)] cursor-pointer text-sm text-[var(--sc-text)]">
                <input
                  type="checkbox"
                  className={field.checkbox}
                  checked={enabledSections.has(s.key)}
                  onChange={() => toggleSection(s.key)}
                />
                {s.label}
              </label>
            ))}
          </div>
          <div className="pt-3 flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setShowExportModal(false)}>Anuluj</Button>
            <Button size="sm" onClick={() => handleDownload(enabledSections)}>Pobierz PDF</Button>
          </div>
        </ModalBody>
      </Modal>

      <form onSubmit={handleSubmit} className="space-y-4">
        <GeneratedDocumentsList reportId={letterId} type="CL" />
        <div>
          <label className={labelCls}>Tytuł:</label>
          <input type="text" name="studyName" value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Typ wizyty:</label>
          <input type="text" name="reportName" value={visitType} onChange={(e) => setVisitType(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t("mainResearcher")}:</label>
          <input type="text" name="mainResearcher" value={confirmationLetter.mainResearcher} onChange={handleChange} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t("protocolNumber")}:</label>
          <input type="text" name="protocolNumber" value={confirmationLetter.protocolNumber} onChange={handleChange} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t("resortNumber")}:</label>
          <input type="text" name="resortNumber" value={confirmationLetter.resortNumber} onChange={handleChange} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>
            {t("introductionText")}:
            <ItemPopover text="Szanowny Panie/Pani + tytuł naukowy, np. Szanowna Pani Profesor/Szanowny Panie Docencie" />
          </label>
          <input type="text" name="introductionText" value={confirmationLetter.introductionText} onChange={handleChange} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>
            {t("visitType")}:
            <ItemPopover text="Wpisz typ wizyty w bierniku tj. inicjującą, monitorującą, zamykającą, zdalną" />
          </label>
          <input type="text" name="visitType" value={confirmationLetter.visitType} onChange={handleChange} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t("visitDay")}:</label>
          <input type="text" name="visitDay" value={confirmationLetter.visitDay} onChange={handleChange} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t("visitHour")}:</label>
          <input type="text" name="visitHour" value={confirmationLetter.visitHour} onChange={handleChange} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t("researchTeam")}:</label>
          <textarea name="researchTeam" value={confirmationLetter.researchTeam} onChange={handleChange} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>{t("visitReason")}:</label>
          <textarea name="visitReason" value={confirmationLetter.visitReason} onChange={handleChange} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>
            zespół CRO:
            <ItemPopover text="Imię, nazwisko, rola w badaniu, numer telefonu, adres e-mail, np. Jan Kowalski CRA 608 700 200 jankowalski@o2.pl" />
          </label>
          <textarea name="croTeam" value={confirmationLetter.croTeam} onChange={handleChange} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Footer Text:</label>
          <input type="text" name="footerText" value={footerText} onChange={(e) => setFooterText(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Version Text:</label>
          <input type="text" name="versionText" value={versionText} onChange={(e) => setVersionText(e.target.value)} className={inputCls} />
        </div>
        <div className="flex gap-2">
          <Button type="submit">{t("save")}</Button>
          <Button variant="secondary" type="button" onClick={() => setShowExportModal(true)}>
            Eksportuj do PDF
          </Button>
        </div>
      </form>
    </>
  );

  if (isModal) return form;

  return (
    <div className="p-6 bg-[#F7F7F5] rounded-[12px] max-w-lg mx-auto m-5">
      <h1 className="text-[15px] font-semibold text-[var(--sc-text)] mb-4">
        {t("confirmationLetter")} {t("for")} {confirmationLetter.reportName}{" "}
        {confirmationLetter.resortName} {confirmationLetter.studyName}
      </h1>
      {form}
    </div>
  );
}

export function ConfirmationLetterPage() {
  const { letterId } = useParams();
  return <ConfirmationLetterContent letterId={letterId} />;
}

export function ConfirmationLetterModal({
  reportId,
  open,
  onClose,
}: {
  reportId: string;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Modal open={open} onClose={onClose} size="md" offset="none">
      <ModalHeader title="Confirmation Letter" onClose={onClose} />
      <ModalBody>
        <ConfirmationLetterContent letterId={reportId} isModal />
      </ModalBody>
    </Modal>
  );
}
